import api from "./api";
import axios from "axios";
import type { PaymentReceivedRow } from "../context/paymentsTypes";

/** Matches `PaymentController::createPaymentAccount` validation. */
export type CreatePaymentAccountPayload = {
  name: string;
  email: string;
  business_name: string;
  /** Sent when available so the backend can resolve company without relying only on middleware. */
  company_id?: number | string;
};

export type CreatePaymentAccountResult = {
  url: string;
  accountId?: string;
};

function extractAccountLinkUrl(payload: unknown): string | null {
  if (payload == null || typeof payload !== "object") return null;
  const root = payload as Record<string, unknown>;

  const tryObj = (o: Record<string, unknown>): string | null => {
    const keys = [
      "account_link_url",
      "xonder_url",
      "onboarding_url",
      "url",
      "link",
    ] as const;
    for (const k of keys) {
      const v = o[k];
      if (typeof v === "string" && v.startsWith("http")) return v;
    }
    return null;
  };

  const direct = tryObj(root);
  if (direct) return direct;

  const data = root.data;
  if (data && typeof data === "object") return tryObj(data as Record<string, unknown>);

  return null;
}

function extractAccountId(payload: unknown): string | undefined {
  if (payload == null || typeof payload !== "object") return undefined;
  const root = payload as Record<string, unknown>;
  for (const key of ["account_id", "xonder_id"] as const) {
    const id = root[key];
    if (typeof id === "string" && id.trim()) return id.trim();
    if (typeof id === "number") return String(id);
  }
  const data = root.data;
  if (data && typeof data === "object") {
    const innerObj = data as Record<string, unknown>;
    for (const key of ["account_id", "xonder_id"] as const) {
      const inner = innerObj[key];
      if (typeof inner === "string" && inner.trim()) return inner.trim();
      if (typeof inner === "number") return String(inner);
    }
  }
  return undefined;
}

function messageFromLaravelBody(data: unknown): string | null {
  if (data == null || typeof data !== "object") return null;
  const d = data as Record<string, unknown>;
  const err = d.error;
  if (err && typeof err === "object") {
    const e = err as Record<string, unknown>;
    if (typeof e.message === "string" && e.message.trim()) {
      const base = e.message.trim();
      const errors = e.errors;
      if (errors && typeof errors === "object") {
        const firstField = Object.values(errors as Record<string, unknown>).find(
          (v) => v != null
        );
        if (Array.isArray(firstField) && firstField[0] && typeof firstField[0] === "string") {
          return `${base}: ${firstField[0]}`;
        }
        if (typeof firstField === "string") return `${base}: ${firstField}`;
      }
      if (typeof e.details === "string" && e.details.trim()) {
        return `${base} (${e.details.trim()})`;
      }
      return base;
    }
  }
  if (typeof d.message === "string" && d.message.trim()) return d.message.trim();
  return null;
}

function getCreatePaymentAccountErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err) && err.response?.data) {
    const fromBody = messageFromLaravelBody(err.response.data);
    if (fromBody) return fromBody;
    if (typeof err.response.data === "string") return err.response.data;
  }
  if (err instanceof Error) return err.message;
  return "Could not start payment setup.";
}

/**
 * `POST /admin/payments/setup` — Laravel `payments` prefix group + `setup` route.
 * If your routes omit the `admin` segment, change the path to `/payments/setup`.
 */
export async function createPaymentAccount(
  body: CreatePaymentAccountPayload
): Promise<CreatePaymentAccountResult> {
  const response = await api.post("/admin/payments/setup", body);
  const url = extractAccountLinkUrl(response.data);
  if (!url) {
    const msg = messageFromLaravelBody(response.data) || "No account link URL returned from the server.";
    throw new Error(msg);
  }
  return { url, accountId: extractAccountId(response.data) };
}

/** Raw payment row from `GET /admin/payments` (Charge on connected account). */
export type ApiPaymentRow = {
  id: string;
  amount: number;
  currency: string;
  status: string;
  payment_method: string | string[] | null;
  customer: string | null;
  created_at: string;
};

export type FetchPaymentsParams = {
  limit?: number;
  starting_after?: string;
  /** `Y-m-d` or ISO date — passed to Laravel `from` / `to`. */
  from?: string;
  to?: string;
};

export type FetchPaymentsResult = {
  payments: ApiPaymentRow[];
  hasMore: boolean;
  nextCursor: string | null;
};

function formatPaymentMethod(value: ApiPaymentRow["payment_method"]): string {
  if (Array.isArray(value) && value.length > 0) {
    return value.map((m) => String(m).replace(/_/g, " ")).join(", ");
  }
  if (typeof value === "string" && value.trim()) {
    return value.trim().replace(/_/g, " ");
  }
  return "—";
}

function formatCustomerId(customer: string | null | undefined): string | null {
  if (customer == null || customer === "") return null;
  const id = String(customer).trim();
  return id.length > 0 ? id : null;
}

/** Stripe amounts are in minor units (e.g. pence). */
export function mapApiPaymentToRow(row: ApiPaymentRow): PaymentReceivedRow {
  const currency = (row.currency || "gbp").toUpperCase();
  const amountMajor = typeof row.amount === "number" ? row.amount / 100 : 0;
  const status =
    typeof row.status === "string"
      ? row.status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
      : "—";

  return {
    id: row.id,
    reference: row.id,
    customer: formatCustomerId(row.customer),
    paymentMethod: formatPaymentMethod(row.payment_method),
    amount: amountMajor,
    currency,
    status,
    receivedAt: row.created_at || "—",
  };
}

export async function fetchPayments(params: FetchPaymentsParams = {}): Promise<FetchPaymentsResult> {
  const response = await api.get("/admin/payments", {
    params: {
      limit: params.limit ?? 10,
      ...(params.starting_after ? { starting_after: params.starting_after } : {}),
      ...(params.from ? { from: params.from } : {}),
      ...(params.to ? { to: params.to } : {}),
    },
  });

  const data = response.data as Record<string, unknown>;
  const rawList = Array.isArray(data.payments) ? (data.payments as ApiPaymentRow[]) : [];
  const hasMore = Boolean(data.has_more);
  const nextCursor =
    typeof data.next_cursor === "string" && data.next_cursor.trim()
      ? data.next_cursor.trim()
      : null;

  return { payments: rawList, hasMore, nextCursor };
}

function getPaymentsApiErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err) && err.response?.data) {
    const fromBody = messageFromLaravelBody(err.response.data);
    if (fromBody) return fromBody;
    if (typeof err.response.data === "string") return err.response.data;
  }
  if (err instanceof Error) return err.message;
  return "Could not load payments.";
}

export { getCreatePaymentAccountErrorMessage, getPaymentsApiErrorMessage };
